import { PermissionAccessLevel } from '@ardoq/api-types';
import { colors } from '@ardoq/design-tokens';
import { IconName, getPermissionLevelIcon } from '@ardoq/icons';

type AccessLevelDisplayData = {
  label: string;
  color: AccessLevelColors;
  iconName: IconName;
  description: string;
};

const PERMISSION_ACCESS_LEVEL_LABEL_MAP: Record<PermissionAccessLevel, string> =
  {
    [PermissionAccessLevel.ADMIN]: 'Full access',
    [PermissionAccessLevel.EDIT]: 'Can edit',
    [PermissionAccessLevel.READ]: 'Can view',
  } as const;

const PERMISSION_ACCESS_LEVEL_DESCRIPTION_MAP: Record<
  PermissionAccessLevel,
  string
> = {
  [PermissionAccessLevel.ADMIN]: 'Has full access to all resources',
  [PermissionAccessLevel.EDIT]: 'Can edit existing resources',
  [PermissionAccessLevel.READ]: 'Can view resources',
} as const;

const getAccessLevelLabel = (permission: PermissionAccessLevel): string => {
  return PERMISSION_ACCESS_LEVEL_LABEL_MAP[permission];
};

const getAccessLevelDescription = (
  permission: PermissionAccessLevel
): string => {
  return PERMISSION_ACCESS_LEVEL_DESCRIPTION_MAP[permission];
};

const PERMISSION_ACCESS_LEVEL_COLORS_MAP: Record<
  PermissionAccessLevel,
  AccessLevelColors
> = {
  [PermissionAccessLevel.ADMIN]: {
    icon: colors.red800,
    background: colors.red50,
    border: colors.red200,
  },
  [PermissionAccessLevel.EDIT]: {
    icon: colors.brand600,
    background: colors.brand50,
    border: colors.brand200,
  },
  [PermissionAccessLevel.READ]: {
    icon: colors.yellow800,
    background: colors.yellow50,
    border: colors.yellow500,
  },
} as const;

type AccessLevelColors = {
  icon: string;
  background: string;
  border: string;
};

const getAccessLevelColor = (
  accessLevel: PermissionAccessLevel
): AccessLevelColors => {
  return PERMISSION_ACCESS_LEVEL_COLORS_MAP[accessLevel];
};

const getAccessLevelDisplayData = (
  accessLevel: PermissionAccessLevel
): AccessLevelDisplayData => {
  return {
    label: getAccessLevelLabel(accessLevel),
    color: getAccessLevelColor(accessLevel),
    iconName: getPermissionLevelIcon(accessLevel),
    description: getAccessLevelDescription(accessLevel),
  };
};

const getExtendedAccessLevelDisplayData = (
  accessLevel: PermissionAccessLevel | null | undefined
): AccessLevelDisplayData => {
  if (!accessLevel) {
    return {
      label: 'No access',
      color: {
        icon: colors.transparent0,
        background: colors.transparent0,
        border: colors.grey300,
      },
      iconName: IconName.NONE,
      description: 'No direct permissions assigned',
    };
  }

  return getAccessLevelDisplayData(accessLevel);
};

export const permissionAccessLevelOperations = {
  getAccessLevelLabel,
  getAccessLevelColor,
  getAccessLevelDisplayData,
  getExtendedAccessLevelDisplayData,
};
